import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUserTie, faUsers, faUserClock, faBriefcase, 
  faIndustry, faHardHat, faCalendarAlt, faLaptopHouse
} from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';

// Optimized animation variants for better performance
const fadeIn = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1, 
    transition: { 
      duration: 0.4, 
      ease: "easeOut" 
    } 
  }
};

const fadeInUp = {
  hidden: { opacity: 0, y: 15 },
  visible: { 
    opacity: 1, 
    y: 0, 
    transition: { 
      duration: 0.4, 
      ease: [0.1, 0.4, 0.2, 1] // optimized cubic-bezier for better performance
    } 
  }
};

const staggerContainer = {
  hidden: { opacity: 1 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.05
    }
  }
};

class Services extends React.Component {
  state = {
    activeTab: 'staffing'
  };

  setActiveTab = (tab) => {
    this.setState({ activeTab: tab });
  }

  render() {
    const { activeTab } = this.state;

    const staffingServices = [
      {
        icon: faUserTie,
        title: "Permanent Staffing",
        description: "Locate highly skilled individuals that share your company's values and objectives.",
        color: "primary"
      },
      {
        icon: faUserClock,
        title: "Temporary & Contract Staffing",
        description: "Employ temporary workers for particular tasks or demand throughout certain seasons.",
        color: "secondary"
      },
      {
        icon: faBriefcase,
        title: "Executive Search & Leadership Hiring",
        description: "Find elite executives and management experts.",
        color: "accent"
      },
      {
        icon: faIndustry,
        title: "Industry-Specific Recruitment",
        description: "Tailored employment solutions for manufacturing, retail, healthcare, IT, and finance, among other industries.",
        color: "primary"
      }
    ];

    const manpowerServices = [
      {
        icon: faUsers,
        title: "Bulk Hiring & Mass Recruitment",
        description: "Efficient large-scale hiring for industries like manufacturing, logistics, and retail.",
        color: "primary"
      },
      {
        icon: faHardHat,
        title: "Skilled & Semi-Skilled Workforce",
        description: "Trained professionals for construction, hospitality, healthcare, and IT sectors.",
        color: "secondary"
      },
      {
        icon: faCalendarAlt,
        title: "Temporary & Contractual Manpower",
        description: "Flexible workforce solutions for short-term projects and seasonal demand.",
        color: "accent"
      },
      {
        icon: faLaptopHouse,
        title: "Onsite & Remote Staffing",
        description: "Deploy talent as per your business requirements.",
        color: "primary"
      }
    ];

    // Helper function to get the correct color class
    const getBgColorClass = (color) => {
      if (color === 'primary') return 'bg-primary';
      if (color === 'secondary') return 'bg-secondary';
      if (color === 'accent') return 'bg-accent';
      return 'bg-gray-200';
    };

    const getBgColorClassWithOpacity = (color) => {
      if (color === 'primary') return 'bg-primary/10';
      if (color === 'secondary') return 'bg-secondary/10';
      if (color === 'accent') return 'bg-accent/10';
      return 'bg-gray-200/10';
    };

    const getTextColorClass = (color) => {
      if (color === 'primary') return 'text-primary';
      if (color === 'secondary') return 'text-secondary';
      if (color === 'accent') return 'text-accent';
      return 'text-gray-700';
    };

    return (
      <div className="pt-20">
        {/* Hero Section */}
        <section className="relative py-24 bg-gradient-to-r from-primary/10 to-secondary/10">
          <div className="container">
            <div className="max-w-4xl mx-auto text-center">
              <motion.h1
                initial="hidden"
                animate="visible"
                variants={fadeInUp}
                className="section-title"
              >
                Our Services
              </motion.h1>
              <motion.p
                initial="hidden"
                animate="visible"
                variants={fadeIn}
                className="text-lg md:text-xl text-gray-700 mt-8"
              >
                Comprehensive staffing and manpower solutions tailored to your business needs.
              </motion.p>
            </div>
          </div>
        </section>

        {/* Service Tabs - Optimized animations */}
        <section className="section">
          <div className="container">
            <div className="flex flex-col md:flex-row justify-center space-y-4 md:space-y-0 md:space-x-8 mb-16">
              <motion.button
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => this.setActiveTab('staffing')}
                className={`px-8 py-4 rounded-full text-lg font-medium transition-all duration-300 ${
                  activeTab === 'staffing'
                    ? 'bg-primary text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Staffing Services
              </motion.button>
              
              <motion.button
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.1 }}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => this.setActiveTab('manpower')}
                className={`px-8 py-4 rounded-full text-lg font-medium transition-all duration-300 ${
                  activeTab === 'manpower'
                    ? 'bg-secondary text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Manpower Services
              </motion.button>
            </div>

            {/* Service Content - Improved animations */}
            <AnimatePresence mode="wait" initial={false}>
              {activeTab === 'staffing' ? (
                <motion.div
                  key="staffing"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.25 }}
                >
                  <div className="mb-12 max-w-4xl mx-auto text-center">
                    <h2 className="text-3xl font-bold mb-6">Staffing Services</h2>
                    <p className="text-lg text-gray-700">
                      The secret to a successful business is identifying the appropriate talent. Our end-to-end staffing solutions at Skill Point Consultants assist companies in hiring the top applicants from a variety of industries. We guarantee the best fit for your company's requirements, whether you're searching for contract employment, temporary workers, or permanent employees.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                    {staffingServices.map((service, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ 
                          duration: 0.3, 
                          delay: index * 0.05,
                          ease: [0.1, 0.4, 0.2, 1]
                        }}
                        whileHover={{ y: -8, transition: { duration: 0.2 } }}
                        className="card hover:shadow-xl transition-all duration-300"
                      >
                        <div className={`h-2 ${getBgColorClass(service.color)}`}></div>
                        <div className="p-6">
                          <div className={`w-16 h-16 rounded-lg ${getBgColorClassWithOpacity(service.color)} flex items-center justify-center mb-4`}>
                            <FontAwesomeIcon icon={service.icon} className={`text-2xl ${getTextColorClass(service.color)}`} />
                          </div>
                          <h3 className="text-xl font-bold mb-3">{service.title}</h3>
                          <p className="text-gray-600">🔹 {service.description}</p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="manpower"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.25 }}
                >
                  <div className="mb-12 max-w-4xl mx-auto text-center">
                    <h2 className="text-3xl font-bold mb-6">Manpower Services</h2>
                    <p className="text-lg text-gray-700">
                      The success of a firm is fueled by its personnel. With a focus on personnel solutions, Skill Point Consultants assists businesses in locating and managing qualified workers for a range of industrial demands. We offer dependable workforce solutions customized for your company, whether you require temporary workers, specialized labour, or bulk employment.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                    {manpowerServices.map((service, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ 
                          duration: 0.3, 
                          delay: index * 0.05,
                          ease: [0.1, 0.4, 0.2, 1]
                        }}
                        whileHover={{ y: -8, transition: { duration: 0.2 } }}
                        className="card hover:shadow-xl transition-all duration-300"
                      >
                        <div className={`h-2 ${getBgColorClass(service.color)}`}></div>
                        <div className="p-6">
                          <div className={`w-16 h-16 rounded-lg ${getBgColorClassWithOpacity(service.color)} flex items-center justify-center mb-4`}>
                            <FontAwesomeIcon icon={service.icon} className={`text-2xl ${getTextColorClass(service.color)}`} />
                          </div>
                          <h3 className="text-xl font-bold mb-3">{service.title}</h3>
                          <p className="text-gray-600">🔹 {service.description}</p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </section>

        {/* Process Section */}
        <section className="section bg-gray-50">
          <div className="container">
            <motion.h2
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              className="section-title"
            >
              Our Recruitment Process
            </motion.h2>

            <div className="max-w-5xl mx-auto">
              <div className="relative">
                {/* Process Timeline */}
                <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-primary/20"></div>
                
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={staggerContainer}
                  className="relative space-y-16"
                >
                  <motion.div variants={fadeInUp} className="relative">
                    <div className="flex items-center justify-center mb-4">
                      <div className="z-10 w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center text-xl font-bold">1</div>
                    </div>
                    <div className="card text-center p-6">
                      <h3 className="text-xl font-bold mb-3">Understanding Requirements</h3>
                      <p className="text-gray-600">We begin by thoroughly understanding your business, culture, and specific hiring needs.</p>
                    </div>
                  </motion.div>
                  
                  <motion.div variants={fadeInUp} className="relative">
                    <div className="flex items-center justify-center mb-4">
                      <div className="z-10 w-10 h-10 rounded-full bg-secondary text-white flex items-center justify-center text-xl font-bold">2</div>
                    </div>
                    <div className="card text-center p-6">
                      <h3 className="text-xl font-bold mb-3">Sourcing & Screening</h3>
                      <p className="text-gray-600">We identify and screen candidates using our extensive database and network.</p>
                    </div>
                  </motion.div>
                  
                  <motion.div variants={fadeInUp} className="relative">
                    <div className="flex items-center justify-center mb-4">
                      <div className="z-10 w-10 h-10 rounded-full bg-accent text-white flex items-center justify-center text-xl font-bold">3</div>
                    </div>
                    <div className="card text-center p-6">
                      <h3 className="text-xl font-bold mb-3">Assessment & Shortlisting</h3>
                      <p className="text-gray-600">Candidates are thoroughly assessed and the best matches are shortlisted.</p>
                    </div>
                  </motion.div>
                  
                  <motion.div variants={fadeInUp} className="relative">
                    <div className="flex items-center justify-center mb-4">
                      <div className="z-10 w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center text-xl font-bold">4</div>
                    </div>
                    <div className="card text-center p-6">
                      <h3 className="text-xl font-bold mb-3">Selection & Onboarding</h3>
                      <p className="text-gray-600">Final selection is made and we assist with the onboarding process.</p>
                    </div>
                  </motion.div>
                </motion.div>
              </div>
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <section className="section py-20 bg-gradient-to-r from-[#B01C1C] via-[#483D8B] to-[#00A7D1] text-white">
          <div className="container text-center">
            <motion.h2
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              className="text-3xl md:text-4xl font-bold mb-6 text-white"
            >
              Ready to Boost Your Workforce?
            </motion.h2>
            <motion.p
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              className="text-lg text-white/90 mb-10 max-w-3xl mx-auto"
            >
              Contact us today to discuss how our staffing and manpower services can benefit your business.
            </motion.p>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <a href="/contact" className="inline-block px-6 py-3 bg-[#B01C1C] hover:bg-[#C02C2C] text-white font-medium rounded-md transition-all duration-300">
                Get a Free Consultation
              </a>
            </motion.div>
          </div>
        </section>
      </div>
    );
  }
}

export default Services; 